// All API access in one place: fetch client + auth/users/faqs calls.
import { API_BASE } from "./config";
import { netLoading } from "./helpers";

let TOKEN = null;
export function setToken(t) { TOKEN = t; }
let onUnauthorized = null;
export function setUnauthorizedHandler(fn) { onUnauthorized = fn; }
function handleUnauthorized(res, path) {
  // A revoked/expired session returns 401 — log the user out (skip for the login call itself).
  if (res && (res.status === 401 || res.status === 403) && TOKEN && !path.includes("/auth/login") && typeof onUnauthorized === "function") {
    onUnauthorized();
  }
}

async function request(path, { method = "GET", body, params, silent = false } = {}) {
  // `silent: true` skips the app-wide loading overlay. Used by background pollers (the 10–60s
  // auto-refreshers on Inventory meta, Productivity, Audit log, Allocations, Sessions, Archive count)
  // so an untouched screen never flashes the centered spinner on its own.
  if (!silent) netLoading.bump(1);
  try {
    const url = new URL(API_BASE + path);
    if (params) Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, v);
    });
    let res;
    try {
      res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", ...(TOKEN ? { Authorization: "Bearer " + TOKEN } : {}) },
        body: body ? JSON.stringify(body) : undefined,
      });
    } catch (networkErr) {
      throw new Error("Can't reach the server. Is the backend running on " + API_BASE + "?");
    }
    if (!res.ok) {
      handleUnauthorized(res, path);
      let message = "Request failed";
      try { const j = await res.json(); message = j.message || j.error || message; } catch {}
      const e = new Error(`${message} (HTTP ${res.status})`); e.status = res.status; throw e;
    }
    if (res.status === 204) return null;
    const text = await res.text();
    return text ? JSON.parse(text) : null;
  } finally {
    if (!silent) netLoading.bump(-1);
  }
}

/** multipart/form-data upload (used by the patient-record import). */
async function upload(path, file, field = "file") {
  const fd = new FormData();
  fd.append(field, file);
  let res;
  try {
    res = await fetch(API_BASE + path, {
      method: "POST",
      headers: { ...(TOKEN ? { Authorization: "Bearer " + TOKEN } : {}) }, // no Content-Type: browser sets the boundary
      body: fd,
    });
  } catch {
    throw new Error("Can't reach the server. Is the backend running on " + API_BASE + "?");
  }
  if (!res.ok) {
    let message = "Upload failed";
    try { const j = await res.json(); message = j.message || j.error || message; } catch {}
    const e = new Error(`${message} (HTTP ${res.status})`); e.status = res.status; throw e;
  }
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

/** Upload with real progress (XHR). onProgress(pct 0-100, phase) — phase "uploading" then "processing". */
function uploadWithProgress(path, file, onProgress, field = "file", extra = null, onStart = null) {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append(field, file);
    if (extra) Object.entries(extra).forEach(([k, v]) => { if (v != null) fd.append(k, v); });
    const xhr = new XMLHttpRequest();
    if (onStart) onStart(xhr);   // let the caller hold the xhr so it can .abort()
    xhr.onabort = () => { const e = new Error("cancelled"); e.cancelled = true; reject(e); };
    xhr.open("POST", API_BASE + path);
    if (TOKEN) xhr.setRequestHeader("Authorization", "Bearer " + TOKEN);
    xhr.upload.onprogress = (e) => {
      if (!onProgress || !e.lengthComputable) return;
      const pct = Math.round((e.loaded / e.total) * 100);
      onProgress(pct, pct >= 100 ? "processing" : "uploading");
    };
    xhr.upload.onload = () => onProgress && onProgress(100, "processing");
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(xhr.responseText ? JSON.parse(xhr.responseText) : null); } catch { resolve(null); }
      } else {
        let message = "Upload failed";
        try { const j = JSON.parse(xhr.responseText); message = j.message || j.error || message; } catch {}
        const err = new Error(`${message} (HTTP ${xhr.status})`); err.status = xhr.status; reject(err);
      }
    };
    xhr.onerror = () => reject(new Error("Can't reach the server. Is the backend running on " + API_BASE + "?"));
    xhr.send(fd);
  });
}

export const api = {
  // auth
  login: async (email, password) => {
    const r = await request("/api/auth/login", { method: "POST", body: { email, password } });
    TOKEN = r.token;
    return r;
  },
  me: () => request("/api/auth/me"),
  forgotPassword: (email) => request("/api/auth/forgot-password", { method: "POST", body: { email } }),
  resetPassword: (email, code, password) => request("/api/auth/reset-password", { method: "POST", body: { email, code, password } }),
  changePassword: (currentPassword, newPassword) => request("/api/auth/change-password", { method: "POST", body: { currentPassword, newPassword } }),

  // users (super admin)
  listUsers: () => request("/api/users"),
  createUser: (b) => request("/api/users", { method: "POST", body: b }),
  updateUser: (id, b) => request(`/api/users/${id}`, { method: "PUT", body: b }),
  deleteUser: (id) => request(`/api/users/${id}`, { method: "DELETE" }),
  sendOtp: (id) => request(`/api/users/${id}/send-otp`, { method: "POST" }),
  verifyOtp: (id, code) => request(`/api/users/${id}/verify-otp`, { method: "POST", body: { code } }),
  setPassword: (id, password) => request(`/api/users/${id}/set-password`, { method: "POST", body: { password } }),
  resetUserPassword: (id, password) => request(`/api/users/${id}/reset-password`, { method: "POST", body: { password } }),
  setStatus: (id, status) => request(`/api/users/${id}/status`, { method: "PATCH", params: { status } }),

  // faqs
  publishedFaqs: () => request("/api/faqs/published"),
  listFaqs: () => request("/api/faqs"),
  saveFaq: (id, body) => request(id ? `/api/faqs/${id}` : "/api/faqs", { method: id ? "PUT" : "POST", body }),
  setFaqPublished: (id, published) => request(`/api/faqs/${id}/publish?published=${published}`, { method: "PATCH" }),
  deleteFaq: (id) => request(`/api/faqs/${id}`, { method: "DELETE" }),

  // reference data (locations + dropdown options)
  getRefData: (opts) => request("/api/ref", opts),

  // patient records
  importPatientRecords: (file, sheet) => upload(`/api/patient-records/import?sheet=${encodeURIComponent(sheet)}`, file),
  importPatientRecordsProgress: (file, sheet, onProgress, forceUids, onStart, token) => uploadWithProgress(`/api/patient-records/import?sheet=${encodeURIComponent(sheet)}${token ? `&token=${encodeURIComponent(token)}` : ""}`, file, onProgress, "file", forceUids && forceUids.length ? { forceUids: forceUids.join("\n") } : null, onStart),
  // TEMPORARY (test phase): bulk-assign from a sheet
  bulkAssignSheet: (file, sheet) => upload(`/api/bulk-assign?sheet=${encodeURIComponent(sheet)}`, file, "file"),
  cancelImport: (token) => request(`/api/patient-records/import/cancel?token=${encodeURIComponent(token)}`, { method: "POST", body: {} }),
  pauseImport: (token) => request(`/api/patient-records/import/pause?token=${encodeURIComponent(token)}`, { method: "POST", body: {} }),
  resumeImport: (token) => request(`/api/patient-records/import/resume?token=${encodeURIComponent(token)}`, { method: "POST", body: {} }),
  listPatientRecords: (sheet, light) => request("/api/patient-records", { params: { sheet, light } }),
  getRecord: (id) => request(`/api/patient-records/${id}`),
  // A record as the CURRENT user worked it (their own round) — for opening from My Work.
  getRecordMine: (id) => request(`/api/patient-records/${id}/as-mine`),
  // #3: other users allocated the same unique id (coordination notice)
  coAllocated: (id, opts) => request(`/api/patient-records/${id}/co-allocated`, opts),
  listAssignees: (sheet, group) => request(`/api/patient-records/assignees?sheet=${encodeURIComponent(sheet||"")}&group=${encodeURIComponent(group||"")}`),
  assignRecords: (recordIds, userId) => request("/api/patient-records/assign", { method: "POST", body: { recordIds, userId } }),
  unassignRecords: (recordIds) => request("/api/patient-records/unassign", { method: "POST", body: { recordIds } }),
  workedLight: (since, opts) => request("/api/patient-records/worked-light" + (since ? "?since=" + encodeURIComponent(since) : ""), opts),
  historicalRecords: () => request("/api/patient-records/historical-light"),
  // #5: move Historical records to Modification / More Info (unassigned)
  moveStage: (recordIds, stage) => request("/api/patient-records/move-stage", { method: "POST", body: { recordIds, stage } }),
  // Daily import value-reconciliation gate (admin): list pending conflicts + resolve one.
  valueConflicts: () => request("/api/patient-records/value-conflicts"),
  resolveValueConflict: (id, action, value) => request(`/api/patient-records/value-conflicts/${id}/resolve`, { method: "POST", body: { action, value } }),
  grid: ({ sheet, group, location, status, rtc, portal, assignedTo, orders, clinics, paStatuses, orderMds, issueGroup, q, page, size }) => request(`/api/patient-records/grid?sheet=${encodeURIComponent(sheet)}&group=${encodeURIComponent(group || "All")}&page=${page || 0}&size=${size || 75}${location ? `&location=${encodeURIComponent(location)}` : ""}${status ? `&status=${encodeURIComponent(status)}` : ""}${rtc ? `&rtc=${encodeURIComponent(rtc)}` : ""}${portal ? `&portal=${encodeURIComponent(portal)}` : ""}${assignedTo ? `&assignedTo=${encodeURIComponent(assignedTo)}` : ""}${orders ? `&orders=${encodeURIComponent(orders)}` : ""}${clinics ? `&clinics=${encodeURIComponent(clinics)}` : ""}${paStatuses ? `&paStatuses=${encodeURIComponent(paStatuses)}` : ""}${orderMds ? `&orderMds=${encodeURIComponent(orderMds)}` : ""}${issueGroup ? `&issueGroup=${encodeURIComponent(issueGroup)}` : ""}${q ? `&q=${encodeURIComponent(q)}` : ""}`),
  // `filters` (optional) carries the active column filters so the option lists cascade (narrow to
  // the other active filters). Omit it and the meta behaves exactly as before.
  gridMeta: (sheet, group, status, location, optGroup, filters, opts) => {
    const f = filters || {};
    const qp = (k, v) => (v ? `&${k}=${encodeURIComponent(v)}` : "");
    return request(`/api/patient-records/grid-meta?sheet=${encodeURIComponent(sheet)}&group=${encodeURIComponent(group || "All")}${status ? `&status=${encodeURIComponent(status)}` : ""}${location ? `&location=${encodeURIComponent(location)}` : ""}${optGroup ? `&optGroup=${encodeURIComponent(optGroup)}` : ""}${qp("rtc", f.rtc)}${qp("portal", f.portal)}${qp("assignedTo", f.assignedTo)}${qp("orders", f.orders)}${qp("clinics", f.clinics)}${qp("paStatuses", f.paStatuses)}${qp("orderMds", f.orderMds)}${qp("q", f.q)}`, opts);
  },
  // Constant, log-based "assigned today" set for a user in a process (userId omitted = the caller themselves).
  dayDetail: ({ sheet, userId } = {}) => request(`/api/patient-records/day-detail?sheet=${encodeURIComponent(sheet)}${userId != null ? `&userId=${encodeURIComponent(userId)}` : ""}`),
  // The user's "My work today" — every record they actioned today (assigned, worked, or kept in Reviewed), all processes.
  dayWork: ({ userId } = {}) => request(`/api/patient-records/day-work${userId != null ? `?userId=${encodeURIComponent(userId)}` : ""}`),
  siblings: (sheet, mrn, excludeId) => request(`/api/patient-records/siblings?sheet=${encodeURIComponent(sheet)}&mrn=${encodeURIComponent(mrn)}&excludeId=${excludeId}`),
  statLight: (sheet) => request(`/api/patient-records/stat-light?sheet=${encodeURIComponent(sheet)}`),
  groupCounts: (sheet) => request(`/api/patient-records/group-counts?sheet=${encodeURIComponent(sheet)}`),
  recomputeDerived: () => request("/api/patient-records/recompute-derived", { method: "POST", body: {} }),
  importStatus: () => request("/api/patient-records/import-status"),
  recallExcludedMd: () => request("/api/patient-records/recall-excluded-md", { method: "POST" }),
  resetExcludedMdWork: () => request("/api/patient-records/reset-excluded-md-work", { method: "POST" }),
  casGroups: (opts) => request("/api/patient-records/cas-groups", opts),
  casNames: (group, opts) => request(`/api/patient-records/cas-names?group=${encodeURIComponent(group||"")}`, opts),
  allocationBreakdown: () => request("/api/patient-records/allocation-breakdown"),
  reviewedAging: ({ userId, group, location, process } = {}) => request(`/api/patient-records/reviewed-aging?${[userId ? `userId=${encodeURIComponent(userId)}` : "", group ? `group=${encodeURIComponent(group)}` : "", location ? `location=${encodeURIComponent(location)}` : "", process ? `process=${encodeURIComponent(process)}` : ""].filter(Boolean).join("&")}`),
  reviewHistory: (id, opts) => request(`/api/patient-records/${id}/review-history`, opts),
  priorWork: (id) => request(`/api/patient-records/${id}/prior-work`),
  reviewOpen: (id) => request(`/api/patient-records/${id}/review-open`, { method: "POST", body: {} }),
  reviewCheckin: (id, comment) => request(`/api/patient-records/${id}/review-checkin`, { method: "POST", body: { comment } }),
  dailyStored: (from, to) => request(`/api/patient-records/daily/stored?from=${encodeURIComponent(from || "")}&to=${encodeURIComponent(to || "")}`),
  dailySnapshot: ({ day, from, to } = {}) => request(`/api/patient-records/daily/snapshot?${[day ? `day=${encodeURIComponent(day)}` : "", from ? `from=${encodeURIComponent(from)}` : "", to ? `to=${encodeURIComponent(to)}` : ""].filter(Boolean).join("&")}`, { method: "POST", body: {} }),
  allocations: (from, to) => request(`/api/patient-records/allocations?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  dailyAllocations: (from, to, opts) => request(`/api/patient-records/daily-allocations?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, opts),
  businessToday: () => request(`/api/patient-records/business-today`),
  summary: () => request(`/api/patient-records/summary`),
  clientReport: (group, months) => request(`/api/patient-records/client-report?group=${encodeURIComponent(group)}&months=${encodeURIComponent(months)}`),
  setClientReport: (b) => request(`/api/patient-records/client-report`, { method: "POST", body: b }),
  auditUsers: () => request("/api/patient-records/audit/users"),
  auditPreview: (userId, process, percent) => request(`/api/patient-records/audit/sample?userId=${userId}&process=${encodeURIComponent(process)}&percent=${percent}`),
  auditSend: (body) => request(`/api/patient-records/audit/send`, { method: "POST", body }),
  auditList: () => request(`/api/patient-records/audit/list`),
  auditRecord: (id) => request(`/api/patient-records/audit/record/${id}`),
  recomputeCompleted: () => request(`/api/patient-records/recompute-completed`, { method: "POST" }),
  listSessions: (opts) => request("/api/sessions", opts),
  revokeSession: (id) => request(`/api/sessions/${id}/revoke`, { method: "POST", body: {} }),
  listPatientRecordsPage: (sheet, page, size) => request(`/api/patient-records/list-page?sheet=${encodeURIComponent(sheet)}&page=${page}&size=${size}`),
  listAssignedRecords: (opts) => request("/api/patient-records/assigned", opts),
  // Guidelines for one allocated order, fetched from the separate Onco PriorAuth Assist
  // tool (matched server-side by client group + order name). Returns:
  //   { query, matchType, count, guidelines: [...] }
  recordGuidelines: (id) => request(`/api/patient-records/${id}/guidelines`),
  // "I used this": record the chosen guideline against a patient record (for Super Admin review).
  recordGuidelineUsage: (recordId, payload) => request(`/api/guideline-usage/${recordId}`, { method: "POST", body: payload }),
  // List the guidelines a record's worker(s) marked as used (Super Admin audit view).
  guidelineUsageList: (recordId) => request(`/api/guideline-usage/${recordId}`),
  // Has the current user attached a guideline to this record yet? → { used: true/false }
  guidelineUsedByMe: (recordId, opts) => request(`/api/guideline-usage/${recordId}/mine`, opts),
  // The guideline currently on the record (latest real usage, any worker) → { code, byName, stage } or {}
  guidelineInherited: (recordId, opts) => request(`/api/guideline-usage/${recordId}/inherited`, opts),
  // recordId -> guideline code the current user used (for the Guideline ID column on worked records)
  myGuidelineCodes: () => request(`/api/guideline-usage/my-codes`),
  // recordId -> guideline code used (any user) for the given record ids (admin lists like Historical)
  guidelineCodesForRecords: (ids) => request(`/api/guideline-usage/codes`, { method: "POST", body: { ids } }),
  // Look up a guideline by its 7-series code (Analytics > Guideline Lookup)
  guidelineByCode: (code, opts) => request(`/api/patient-records/guidelines/by-code?code=${encodeURIComponent(code)}`, opts),
  // Report that NO guideline exists for this order (clears the gate + logs to Assist for the authors).
  reportNoGuideline: (recordId, payload) => request(`/api/patient-records/${recordId}/no-guideline`, { method: "POST", body: payload }),
  // Manual "fetch by criteria" search (used when the auto match misses). Pass any
  // subset of: client, specialty, orderType, order, anatomicalLocation, technique.
  guidelinesSearch: (params = {}) => {
    const qs = Object.entries(params)
      .filter(([, v]) => v != null && String(v).trim() !== "")
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join("&");
    return request(`/api/patient-records/guidelines/lookup${qs ? `?${qs}` : ""}`);
  },
  listHelpQueue: () => request("/api/patient-records/help-queue"),
  setRecordStatus: (id, status) => request(`/api/patient-records/${id}/status`, { method: "PATCH", body: { status } }),
  setRecordWorkflow: (id, body) => request(`/api/patient-records/${id}/workflow`, { method: "PATCH", body }),
  setRecordWork: (id, body) => request(`/api/patient-records/${id}/work`, { method: "PATCH", body }),
  addRequest: (id, body) => request(`/api/patient-records/${id}/add-request`, { method: "POST", body }),
  addRequestCheck: (id, orderName) => request(`/api/patient-records/${id}/add-request-check?orderName=${encodeURIComponent(orderName)}`),
  createManualRecord: (body) => request("/api/patient-records/manual", { method: "POST", body }),
  updateManualRecord: (id, body) => request(`/api/patient-records/${id}/manual`, { method: "PUT", body }),
  orderNames: () => request("/api/patient-records/order-names"),
  listOrders: () => request("/api/patient-records/orders-catalog"),
  addOrder: (name) => request("/api/patient-records/orders-catalog", { method: "POST", body: { name } }),
  deleteOrder: (id) => request(`/api/patient-records/orders-catalog/${id}`, { method: "DELETE" }),
  assignRecordsToStage: (recordIds, userId, stage) => request("/api/patient-records/assign-stage", { method: "POST", body: { recordIds, userId, stage } }),
  sendToProduction: (recordIds, group) => request("/api/patient-records/send-to-production", { method: "POST", body: { recordIds, group: group || "LACN" } }),
  // Diagnostic "All records" view — every record imported in the last N days, hidden copies included.
  allImported: (days) => request(`/api/patient-records/all-imported?days=${days || 14}`),
  forceToProduction: (recordIds, group) => request("/api/patient-records/force-to-production", { method: "POST", body: { recordIds, group: group || "LACN" } }),
  clearProduction: (recordIds) => request("/api/patient-records/clear-production", { method: "POST", body: { recordIds } }),
  setPrioritized: (recordIds, value) => request("/api/patient-records/prioritize", { method: "POST", body: { recordIds, value } }),
  reopenRecords: (recordIds) => request("/api/patient-records/reopen", { method: "POST", body: { recordIds } }),
  sendToStage: (recordIds, stage, issueType, issueReason) => request("/api/patient-records/send-to-stage", { method: "POST", body: { recordIds, stage, issueType, issueReason } }),
  setRecordPriority: (recordIds, priority) => request("/api/patient-records/set-priority", { method: "POST", body: { recordIds, priority } }),
  moveToSheet: (recordIds, sheet) => request("/api/patient-records/move-sheet", { method: "POST", body: { recordIds, sheet } }),
  deleteRecords: (recordIds) => request("/api/patient-records/delete-records", { method: "POST", body: { recordIds } }),
  requestArchive: (recordIds, reason) => request("/api/patient-records/request-archive", { method: "POST", body: { recordIds, reason } }),
  approveArchive: (recordIds) => request("/api/patient-records/approve-archive", { method: "POST", body: { recordIds } }),
  rejectArchive: (recordIds) => request("/api/patient-records/reject-archive", { method: "POST", body: { recordIds } }),
  listArchiveRequests: (opts) => request("/api/patient-records/archive-requests", opts),
  listArchived: () => request("/api/patient-records/archived"),
  sheetCounts: () => request("/api/patient-records/counts"),
  myWorked: () => request("/api/patient-records/my-worked"),
  myProductivity: (opts) => request("/api/patient-records/my-productivity", opts),
  reviewActivity: (opts) => request("/api/patient-records/review-activity", opts),
  approvedList: () => request("/api/patient-records/approved"),

  // announcements / posts
  listPosts: () => request("/api/posts"),
  unreadPosts: () => request("/api/posts/unread"),
  createPost: (body) => request("/api/posts", { method: "POST", body }),
  markPostRead: (id) => request(`/api/posts/${id}/read`, { method: "POST" }),
  deletePost: (id) => request(`/api/posts/${id}`, { method: "DELETE" }),

  // super-admin data management
  listGroups: () => request("/api/admin/data/groups"),
  saveGroup: (id, b) => request(id ? `/api/admin/data/groups/${id}` : "/api/admin/data/groups", { method: id ? "PUT" : "POST", body: b }),
  deleteGroup: (id) => request(`/api/admin/data/groups/${id}`, { method: "DELETE" }),

  // dashboards / stats / audit
  statsOverview: () => request("/api/stats/overview"),
  statsProduction: () => request("/api/stats/production"),
  statsMine: () => request("/api/stats/mine"),
  auditSearch: (params, opts) => request("/api/admin/audit", { params, ...(opts || {}) }),
  listRefLocations: () => request("/api/admin/data/locations"),
  saveRefLocation: (id, b) => request(id ? `/api/admin/data/locations/${id}` : "/api/admin/data/locations", { method: id ? "PUT" : "POST", body: b }),
  deleteRefLocation: (id) => request(`/api/admin/data/locations/${id}`, { method: "DELETE" }),
  mapPortal: (body) => request("/api/patient-records/map-portal", { method: "POST", body }),
  listPortals: () => request("/api/admin/data/portals"),
  savePortal: (id, b) => request(id ? `/api/admin/data/portals/${id}` : "/api/admin/data/portals", { method: id ? "PUT" : "POST", body: b }),
  deletePortal: (id) => request(`/api/admin/data/portals/${id}`, { method: "DELETE" }),
  listPayors: () => request("/api/admin/data/payors"),
  savePayor: (id, b) => request(id ? `/api/admin/data/payors/${id}` : "/api/admin/data/payors", { method: id ? "PUT" : "POST", body: b }),
  deletePayor: (id) => request(`/api/admin/data/payors/${id}`, { method: "DELETE" }),
};